import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, Eye, TestTube2, Radio, CheckCircle2, XCircle, ExternalLink, Users, Crown, Flame, CalendarClock, Trash2, Clock } from "lucide-react";

const emptyDraft = { subject: "", heading: "", body: "", cta_label: "", cta_url: "", audience: "all" };

const AUDIENCE_ICON = { all: Users, elite: Crown, top_clickers: Flame };

export function AdminBroadcasts({ client, API }) {
  const [draft, setDraft] = useState(emptyDraft);
  const [previewHtml, setPreviewHtml] = useState("");
  const [recipients, setRecipients] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [audiences, setAudiences] = useState([]);
  const [scheduleAt, setScheduleAt] = useState("");

  const loadAudiences = useCallback(() => {
    client().get(`${API}/admin/broadcasts/audiences`)
      .then((r) => setAudiences(r.data.audiences || []))
      .catch(() => {});
  }, [client, API]);

  const loadHistory = useCallback(() => {
    setLoadingHistory(true);
    client().get(`${API}/admin/broadcasts`)
      .then((r) => setHistory(r.data.broadcasts || []))
      .catch(() => toast.error("Failed to load broadcast history"))
      .finally(() => setLoadingHistory(false));
  }, [client, API]);

  useEffect(() => { loadHistory(); loadAudiences(); }, [loadHistory, loadAudiences]);

  const selectedAudience = audiences.find((a) => a.key === draft.audience);
  const selectedCount = selectedAudience?.count ?? 0;

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const validate = () => {
    if (!draft.subject.trim() || draft.subject.length < 3) return "Subject must be at least 3 characters";
    if (!draft.heading.trim()) return "Heading is required";
    if (!draft.body.trim()) return "Body is required";
    if ((draft.cta_label && !draft.cta_url) || (!draft.cta_label && draft.cta_url)) return "CTA needs both a label AND a URL (or leave both blank)";
    return null;
  };

  const preview = async () => {
    const err = validate();
    if (err) return toast.error(err);
    try {
      const { data } = await client().post(`${API}/admin/broadcasts/preview`, draft);
      setPreviewHtml(data.html);
      setRecipients(data.recipients);
      setShowPreview(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    }
  };

  const sendTest = async () => {
    const err = validate();
    if (err) return toast.error(err);
    if (!testEmail || !testEmail.includes("@")) return toast.error("Enter a valid test email");
    setTesting(true);
    try {
      await client().post(`${API}/admin/broadcasts/test-send`, { ...draft, test_email: testEmail });
      toast.success(`Test email sent to ${testEmail}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test send failed");
    } finally {
      setTesting(false);
    }
  };

  const sendBlast = async () => {
    const err = validate();
    if (err) return toast.error(err);
    const scheduled_at = scheduleAt ? new Date(scheduleAt).toISOString() : null;
    if (scheduled_at && new Date(scheduled_at) <= new Date()) return toast.error("Pick a future date & time to schedule");
    const when = scheduled_at ? `on ${new Date(scheduled_at).toLocaleString()}` : "right now";
    if (!window.confirm(`Send this broadcast to ${selectedCount} ambassador${selectedCount !== 1 ? "s" : ""} (${selectedAudience?.label || draft.audience}) ${when}?\n\nThis cannot be undone.`)) return;
    setSending(true);
    try {
      const { data } = await client().post(`${API}/admin/broadcasts`, { ...draft, scheduled_at });
      if (data.status === "scheduled") {
        toast.success(`Scheduled for ${new Date(data.scheduled_at).toLocaleString()} — ${data.recipients_target} recipient${data.recipients_target !== 1 ? "s" : ""}.`);
      } else {
        toast.success(`Broadcast queued for ${data.recipients_target} ambassadors — sending in the background.`);
      }
      setDraft(emptyDraft);
      setScheduleAt("");
      setShowPreview(false);
      setTimeout(loadHistory, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Broadcast failed");
    } finally {
      setSending(false);
    }
  };

  const cancelBroadcast = async (id) => {
    if (!window.confirm("Cancel this scheduled broadcast? It won't be sent.")) return;
    try {
      await client().delete(`${API}/admin/broadcasts/${id}`);
      toast.success("Scheduled broadcast cancelled");
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    }
  };

  return (
    <div data-testid="admin-broadcasts" className="space-y-6">
      <div className="bg-gradient-to-br from-indigo-600 to-fuchsia-600 text-white rounded-lg p-6">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest mb-2 opacity-90">
          <Radio className="w-4 h-4" /> Ambassador Broadcast
        </div>
        <h2 className="font-heading text-3xl tracking-tight">Announce something to every ambassador</h2>
        <p className="text-sm text-white/80 max-w-2xl mt-2">
          Compose once. We&apos;ll email every active ambassador at a Resend-safe pace and log the result below. Send a test to yourself first if you&apos;re not sure.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 block mb-2">Who gets this?</label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5" data-testid="bc-audience-picker">
            {(audiences.length ? audiences : [{ key: "all", label: "All active ambassadors", count: 0 }, { key: "elite", label: "Elite+ (1,500+ clicks this month)", count: 0 }, { key: "top_clickers", label: "Top 10 clickers this month", count: 0 }]).map((a) => {
              const Icon = AUDIENCE_ICON[a.key] || Users;
              const active = draft.audience === a.key;
              return (
                <button key={a.key} type="button" onClick={() => set("audience", a.key)}
                  data-testid={`bc-audience-${a.key}`}
                  className={`text-left p-3 rounded-lg border transition-colors ${active ? "bg-emerald-50 border-emerald-400 ring-1 ring-emerald-400" : "bg-slate-50 border-slate-200 hover:border-slate-300"}`}>
                  <div className="flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${active ? "text-emerald-600" : "text-slate-500"}`} />
                    <span className="text-2xl font-bold leading-none text-slate-900">{a.count}</span>
                  </div>
                  <p className={`text-xs mt-1.5 font-medium ${active ? "text-emerald-800" : "text-slate-600"}`}>{a.label}</p>
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 block mb-1.5">Subject line</label>
          <input type="text" value={draft.subject} onChange={(e) => set("subject", e.target.value)}
            placeholder="e.g. New elite tier bonuses just dropped" data-testid="bc-subject"
            className="w-full px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 block mb-1.5">Email heading (H1)</label>
          <input type="text" value={draft.heading} onChange={(e) => set("heading", e.target.value)}
            placeholder="e.g. Big changes to your tier bonuses 🎉" data-testid="bc-heading"
            className="w-full px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 block mb-1.5">
            Body (plain text — blank lines become paragraphs, single newlines stay as line breaks)
          </label>
          <textarea rows={10} value={draft.body} onChange={(e) => set("body", e.target.value)}
            placeholder={"Hey there,\n\nWe just rolled out a new bonus structure — Elite tier now earns +2%…\n\nGrab your dashboard and check it out."}
            data-testid="bc-body"
            className="w-full px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono" />
          <p className="text-xs text-slate-400 mt-1">HTML is escaped for safety — write plain text only.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 block mb-1.5">CTA button label (optional)</label>
            <input type="text" value={draft.cta_label} onChange={(e) => set("cta_label", e.target.value)}
              placeholder="e.g. See my new tier" data-testid="bc-cta-label"
              className="w-full px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 block mb-1.5">CTA button URL (optional)</label>
            <input type="url" value={draft.cta_url} onChange={(e) => set("cta_url", e.target.value)}
              placeholder="https://socialaffiliates.io/dashboard" data-testid="bc-cta-url"
              className="w-full px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          </div>
        </div>

        <div className="border-t border-slate-200 pt-4 flex flex-wrap items-center gap-2">
          <button onClick={preview} data-testid="bc-preview"
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-full bg-slate-900 text-white hover:bg-slate-700 transition-colors">
            <Eye className="w-3.5 h-3.5" /> Preview
          </button>
          <div className="inline-flex items-center gap-2 border border-slate-200 rounded-full pl-3 pr-1 py-1 bg-slate-50">
            <input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
              placeholder="rory@socialaffiliates.io" data-testid="bc-test-email"
              className="bg-transparent border-none text-xs focus:outline-none w-52" />
            <button onClick={sendTest} disabled={testing} data-testid="bc-send-test"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-indigo-600 text-white hover:bg-indigo-500 transition-colors disabled:opacity-60">
              {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <TestTube2 className="w-3.5 h-3.5" />} Send test
            </button>
          </div>
          <div className="inline-flex items-center gap-1.5 border border-slate-200 rounded-full pl-3 pr-2 py-1 bg-slate-50" title="Leave blank to send immediately">
            <CalendarClock className="w-3.5 h-3.5 text-slate-500" />
            <input type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)}
              min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
              data-testid="bc-schedule-at"
              className="bg-transparent border-none text-xs focus:outline-none text-slate-700" />
            {scheduleAt && (
              <button onClick={() => setScheduleAt("")} data-testid="bc-schedule-clear"
                className="text-slate-400 hover:text-slate-700 text-sm leading-none px-1" title="Clear schedule">×</button>
            )}
          </div>
          <button onClick={sendBlast} disabled={sending} data-testid="bc-send-all"
            className="ml-auto inline-flex items-center gap-1.5 text-sm font-bold px-5 py-2.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-500 transition-colors disabled:opacity-60">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : scheduleAt ? <CalendarClock className="w-4 h-4" /> : <Send className="w-4 h-4" />}
            {scheduleAt ? `Schedule for ${selectedCount}` : `Send to ${selectedCount} ${draft.audience === "all" ? "ambassadors" : "selected"}`}
          </button>
        </div>
      </div>

      {showPreview && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowPreview(false)} data-testid="bc-preview-modal">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="font-heading text-lg">Email preview</h3>
                <p className="text-xs text-slate-500 mt-0.5">Will send to <b>{recipients}</b> active ambassadors</p>
              </div>
              <button onClick={() => setShowPreview(false)} className="text-slate-400 hover:text-slate-700 text-2xl leading-none">×</button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <iframe title="Email preview" srcDoc={previewHtml} className="w-full h-[600px] border-none" data-testid="bc-preview-iframe" />
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg">Broadcast history</h3>
          <button onClick={loadHistory} className="text-xs text-slate-500 hover:text-slate-800">Refresh</button>
        </div>
        {loadingHistory ? (
          <div className="flex items-center justify-center py-8 text-slate-400"><Loader2 className="w-4 h-4 animate-spin mr-2" />Loading…</div>
        ) : history.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-8">No broadcasts sent yet. Compose your first one above.</p>
        ) : (
          <div className="space-y-2">
            {history.map((b) => (
              <div key={b.id} data-testid={`bc-history-${b.id}`} className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                {b.status === "sent" ? <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  : b.status === "sending" ? <Loader2 className="w-4 h-4 text-indigo-600 animate-spin flex-shrink-0" />
                  : b.status === "scheduled" ? <CalendarClock className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                  : b.status === "cancelled" ? <XCircle className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  : b.status === "test_only" ? <TestTube2 className="w-4 h-4 text-slate-500 flex-shrink-0" />
                  : <XCircle className="w-4 h-4 text-rose-600 flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{b.subject}</p>
                  <p className="text-xs text-slate-500">
                    {b.status === "scheduled" ? (
                      <span className="text-indigo-600 font-semibold inline-flex items-center gap-1"><Clock className="w-3 h-3" />Scheduled for {new Date(b.scheduled_at).toLocaleString()}</span>
                    ) : (
                      <>{new Date(b.created_at).toLocaleString()} · {b.sent_count}/{b.recipients_target || "?"} sent</>
                    )}
                    {b.status === "cancelled" && <span className="text-slate-400"> · cancelled</span>}
                    {b.failed_count > 0 && <span className="text-rose-600"> · {b.failed_count} failed</span>}
                    {b.status === "test_only" && <span className="text-slate-500"> · test only</span>}
                  </p>
                </div>
                {b.status === "scheduled" && (
                  <button onClick={() => cancelBroadcast(b.id)} data-testid={`bc-cancel-${b.id}`}
                    className="text-xs font-semibold text-rose-600 hover:text-rose-800 inline-flex items-center gap-1">
                    <Trash2 className="w-3 h-3" /> Cancel
                  </button>
                )}
                {b.cta_url && (
                  <a href={b.cta_url} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-600 hover:underline inline-flex items-center gap-1">
                    CTA <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
